import { batch } from 'react-redux';
import type { AnyAction, Middleware, ThunkDispatch } from '@reduxjs/toolkit';
import { LOCATION_CHANGE } from 'connected-next-router';
import type { LocationChangeAction } from 'connected-next-router/actions';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { requestProducts } from 'src/redux/products/thunks/requestProducts';
import { TriggerName } from 'src/redux/products/types';
import { extractBasePath } from 'src/utils/extractBasePath';
import { scrollToTopOfProductsList } from 'src/utils/scrollToTopOfProductsList';
import { getFacetChangeMetadata } from '../helpers/getFacetChangeMetadata';
import { getGuaranteedArrivalChangeMetadata } from '../helpers/getGuaranteedArrivalChangeMetadata';
import { getRouteChangeTriggerName } from '../helpers/getRouteChangeTriggerName';
import { productsQueryMatchesURL } from '../helpers/productsQueryMatchesURL';
import { syncFromURL } from '../thunks/syncFromURL';
import type { SyncProductsQueryFromURLPayload } from '../types/ProductsQueryState';

const getActiveSortSignature = (
  sortOptions: ReduxApplicationState['productsQuery']['sortOptions'],
): string | null => {
  const activeSort = sortOptions.find((sortOption) => sortOption.active);

  if (!activeSort || activeSort.key === '') {
    return null;
  }

  return `${activeSort.key}:${activeSort.descending}`;
};

const getActiveFacetValuesByKey = (
  facets: ReduxApplicationState['productsQuery']['facets'],
): Record<string, string[]> => {
  return facets.reduce<Record<string, string[]>>((acc, facet) => {
    const activeValues = facet.options
      .filter((option) => option.active)
      .map((option) => option.value)
      .sort();

    if (activeValues.length > 0) {
      acc[facet.alias] = activeValues;
    }

    return acc;
  }, {});
};

const normalizeActiveFacetValuesByKey = (
  activeFacetValuesByKey: SyncProductsQueryFromURLPayload['activeFacetValuesByKey'],
): Record<string, string[]> => {
  return Object.entries(activeFacetValuesByKey).reduce<Record<string, string[]>>((acc, entry) => {
    const [alias, values] = entry;
    const sortedValues = [...values].sort();
    if (sortedValues.length > 0) {
      acc[alias] = sortedValues;
    }
    return acc;
  }, {});
};

const getFacetValuesSignature = (activeFacetValuesByKey: Record<string, string[]>): string => {
  return Object.entries(activeFacetValuesByKey)
    .sort(([aliasA], [aliasB]) => aliasA.localeCompare(aliasB))
    .map(([alias, values]) => `${alias}:${values.join(',')}`)
    .join('|');
};

const hasSyncPayloadChanges = (
  state: ReduxApplicationState,
  payload: SyncProductsQueryFromURLPayload,
): boolean => {
  const currentProductsQuery = state.productsQuery;

  const currentActiveSort = getActiveSortSignature(currentProductsQuery.sortOptions);
  const nextActiveSort = payload.activeSort
    ? `${payload.activeSort.key}:${payload.activeSort.descending}`
    : null;

  if (currentProductsQuery.page !== payload.page || currentActiveSort !== nextActiveSort) {
    return true;
  }

  const currentSearchTerm = currentProductsQuery.searchTerm ?? undefined;
  const nextSearchTerm = payload.searchTerm ?? undefined;
  if (currentSearchTerm !== nextSearchTerm) {
    return true;
  }

  if (currentProductsQuery.guaranteedArrivalDay !== payload.guaranteedArrivalDay) {
    return true;
  }

  const currentFacetValues = getActiveFacetValuesByKey(currentProductsQuery.facets);
  const nextFacetValues = normalizeActiveFacetValuesByKey(payload.activeFacetValuesByKey);

  return getFacetValuesSignature(currentFacetValues) !== getFacetValuesSignature(nextFacetValues);
};

type ReduxThunkDispatch = ThunkDispatch<ReduxApplicationState, undefined, AnyAction>;

const isLocationChangeAction = (action: AnyAction): action is LocationChangeAction =>
  action.type === LOCATION_CHANGE;

/*
    connected-next-router dispatches LOCATION_CHANGE for every client-side
    navigation (push, replace, shallow routing and popstate). When the new URL
    no longer matches the productsQuery state, sync the state from the URL and,
    if the query meaningfully changed, request products from the listings API.

    Initial state and products are populated server-side in getServerSideProps
    (syncFromURL + requestProducts), so this middleware only handles client
    navigations. Infinite scroll intentionally bypasses this flow: it dispatches
    changePage and requestProducts directly with its own trigger names.
*/
export const productsQueryURLSyncMiddleware: Middleware<
  unknown,
  ReduxApplicationState,
  ReduxThunkDispatch
> =
  ({ dispatch, getState }) =>
  (next) =>
  (action) => {
    // Read before next(action), which is what updates the router slice. Optional
    // because it only feeds tracking: with no previous pathname to compare, the
    // navigation is treated as a refinement and no page event is sent.
    const previousPathname = isLocationChangeAction(action)
      ? getState().router?.location?.pathname
      : undefined;

    const result = next(action);

    if (typeof window === 'undefined' || !isLocationChangeAction(action)) {
      return result;
    }

    const { href } = action.payload.location;
    const previousState = getState();

    /*
      A move to a different taxonomy listing changes the path segments, whereas
      refining the current one only touches the `/-/` filter portion or the query
      string. Both are classified as a facet change, so the trigger name cannot
      distinguish them, but the base paths can. Tracking needs the difference: one
      is a new page, the other is not.
    */
    const isTaxonomyNavigation =
      previousPathname !== undefined &&
      extractBasePath(previousPathname) !== extractBasePath(action.payload.location.pathname);

    if (productsQueryMatchesURL(href, previousState)) {
      return result;
    }

    // Batch the URL→state sync and the products request together. Without this,
    // react-redux notifies subscribers after each dispatch, so a render can land
    // between the facet/selection change and `requestProducts` setting
    // `pageLoading` — a window in which count-derived UI (the taxonomy nav) would
    // briefly render the new selection against the previous page's stale counts.
    // Batching collapses both into a single render so the loading flag is never
    // out of step with the selection.
    batch(() => {
      const payload = (dispatch as ReduxThunkDispatch)(syncFromURL(href));

      scrollToTopOfProductsList();

      if (!hasSyncPayloadChanges(previousState, payload)) {
        return;
      }

      const triggerName = getRouteChangeTriggerName(previousState, getState());
      const metadata =
        triggerName === TriggerName.CHANGE_FACETS || triggerName === TriggerName.RESET_FACET
          ? getGuaranteedArrivalChangeMetadata(previousState.productsQuery, payload) ??
            getFacetChangeMetadata(
              previousState.productsQuery.facets,
              payload.activeFacetValuesByKey,
            )
          : undefined;

      (dispatch as ReduxThunkDispatch)(
        requestProducts({
          isFacetPillSurface: Boolean(previousState.productsQuery.openPillFacetKey),
          isTaxonomyNavigation,
          metadata,
          pageLoading: payload.page,
          triggerName,
        }),
      );
    });

    return result;
  };
